import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { OrganizationSwitcher } from '@/components/organization/OrganizationSwitcher';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NoCodeoLogo } from '@/components/ui/logo';
import { User, LogOut, Menu, X, ShoppingCart, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useCart } from '@/hooks/useCart';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAdminFeatureFlags } from '@/hooks/useAdminFeatureFlags';
import { CartSummary } from '@/components/payment/CartSummary';
import { Badge } from '@/components/ui/badge';
import { TypeformButton } from '@/components/qualiopi/TypeformButton';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';

interface NavChild {
  name: string;
  href: string;
  key?: string;
}

interface NavGroup {
  label: string;
  children: NavChild[];
}

function NavDropdown({ label, children, pathname, onNavigate }: { label: string; children: NavChild[]; pathname: string; onNavigate?: () => void }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isActive = children.some(c => pathname === c.href);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={`px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap rounded-lg flex items-center gap-1 ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 z-50">
          <div className="bg-white rounded-xl shadow-lg border border-slate-100 py-2 min-w-[180px]">
            {children.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-4 py-2.5 text-sm transition-colors duration-200 ${pathname === item.href ? 'text-slate-900 bg-slate-50 font-medium' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`}
                onClick={() => { setOpen(false); onNavigate?.(); }}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNavGroup({ label, children, pathname, onNavigate }: { label: string; children: NavChild[]; pathname: string; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(o => !o)}
        type="button"
      >
        {label}
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pl-4 space-y-0.5 pb-1">
          {children.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`block px-4 py-3 rounded-xl text-sm transition-colors duration-200 ${pathname === item.href ? 'text-slate-900 bg-slate-50 font-medium' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
              onClick={onNavigate}
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navigation() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { getItemsCount } = useCart();
  const { isEnabled } = useFeatureFlags();
  const { isEnabled: isAdminFeatureEnabled } = useAdminFeatureFlags();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const showSubscriptions = isAdminFeatureEnabled('subscriptions_module_enabled');

  const navGroups: NavGroup[] = [
    {
      label: t('nav.services'),
      children: [
        { name: t('nav.conseil'), href: '/conseil' },
        { name: t('nav.formations'), href: '/formations' },
        { name: t('nav.financement'), href: '/financement-opco' },
        { name: t('nav.tarifs'), href: '/tarifs' },
        ...(showSubscriptions ? [{ name: t('nav.abonnements'), href: '/subscriptions', key: 'Abonnements' }] : []),
      ],
    },
    {
      label: t('nav.ressources'),
      children: [
        { name: t('nav.blog'), href: '/blog' },
        { name: t('nav.casClients'), href: '/cas-clients' },
      ],
    },
    {
      label: t('nav.entreprise'),
      children: [
        { name: t('nav.about'), href: '/about' },
        { name: t('nav.contact'), href: '/contact' },
      ],
    },
  ];

  const getInitials = (email: string) => email.substring(0, 2).toUpperCase();

  return (
    <>
      {/* Floating Header Container */}
      <div className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
        <nav
          className={`max-w-[900px] mx-auto bg-white rounded-2xl transition-shadow duration-200 ease-out ${isScrolled ? 'shadow-floating-scrolled' : 'shadow-floating'}`}
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div className="flex items-center flex-shrink-0 space-x-4">
                <Link to="/" className="flex items-center transition-opacity duration-200 hover:opacity-80">
                  <NoCodeoLogo size="header" variant="default" />
                </Link>
                {user && isEnabled('organizations') && <OrganizationSwitcher />}
              </div>

              {/* Desktop Navigation - Grouped Dropdowns */}
              <div className="hidden lg:flex lg:items-center lg:space-x-0">
                {navGroups.map((group) => (
                  <NavDropdown
                    key={group.label}
                    label={group.label}
                    children={group.children}
                    pathname={location.pathname}
                  />
                ))}
              </div>

              {/* Actions - Right */}
              <div className="flex items-center space-x-3">
                <LanguageSwitcher />

                {user && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="relative h-9 w-9 rounded-full hover:bg-slate-100">
                        <ShoppingCart className="h-4 w-4 text-slate-600" />
                        {getItemsCount() > 0 && (
                          <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px]">
                            {getItemsCount()}
                          </Badge>
                        )}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <ShoppingCart className="h-5 w-5" />
                          {t('nav.cart')}
                        </DialogTitle>
                        <DialogDescription>{t('nav.cartDescription')}</DialogDescription>
                      </DialogHeader>
                      <CartSummary />
                    </DialogContent>
                  </Dialog>
                )}

                {user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="relative h-9 w-9 rounded-full hover:bg-slate-100">
                        <Avatar className="h-8 w-8 ring-2 ring-slate-200">
                          <AvatarFallback className="bg-slate-900 text-white text-xs font-medium">
                            {getInitials(user.email || 'U')}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" align="end" forceMount>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center">
                          <User className="mr-2 h-4 w-4" />
                          <span>{t('nav.dashboard')}</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="flex items-center">
                          <User className="mr-2 h-4 w-4" />
                          <span>{t('nav.profile')}</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <div className="w-full">
                          <TypeformButton formType="complaint" variant="outline" size="sm" className="w-full justify-start" />
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={signOut}>
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>{t('nav.logout')}</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <div className="hidden md:flex md:items-center md:space-x-2">
                      <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900 hover:bg-transparent text-sm font-medium">
                        <Link to="/auth">{t('nav.connexion')}</Link>
                      </Button>
                      <Button asChild className="bg-nc-marine text-white hover:bg-nc-turquoise hover:text-nc-marine rounded-full px-5 py-2 text-sm font-medium shadow-sm transition-colors duration-200">
                        <Link to="/conseil#project-brief">{t('nav.cta')}</Link>
                      </Button>
                    </div>
                    <Button asChild className="md:hidden bg-nc-marine text-white hover:bg-nc-turquoise hover:text-nc-marine rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200">
                      <Link to="/conseil#project-brief">{t('nav.ctaShort')}</Link>
                    </Button>
                  </>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden h-9 w-9 p-0 rounded-full hover:bg-slate-100"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5 text-slate-600" /> : <Menu className="h-5 w-5 text-slate-600" />}
                </Button>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden animate-fade-in" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-full max-w-[320px] bg-white z-50 lg:hidden transform transition-transform duration-250 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
            <NoCodeoLogo size="sm" variant="compact" />
            <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(false)} className="h-9 w-9 p-0 rounded-full hover:bg-slate-100">
              <X className="h-5 w-5 text-slate-600" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto py-6">
            <nav className="space-y-1 px-4">
              {navGroups.map((group) => (
                <MobileNavGroup
                  key={group.label}
                  label={group.label}
                  children={group.children}
                  pathname={location.pathname}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              ))}
            </nav>
          </div>

          {!user && (
            <div className="border-t border-slate-100 p-6 space-y-3">
              <Link to="/auth" className="block text-center text-slate-600 hover:text-slate-900 text-sm font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                {t('nav.connexion')}
              </Link>
              <Button asChild className="w-full bg-nc-marine text-white hover:bg-nc-turquoise hover:text-nc-marine rounded-full py-3 text-base font-medium transition-colors duration-200">
                <Link to="/conseil#project-brief" onClick={() => setMobileMenuOpen(false)}>{t('nav.cta')}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
